
// Deployment trigger: 2026-04-24
import React from 'react';
import { Zap, Crown } from 'lucide-react';
import { Prize } from '../../types';

interface SpinWheelProps {
  prizes: Prize[];
  rotation: number;
  gameState: string;
  onStart: () => void;
  theme?: string;
  buttonText?: string;
  isLightMode?: boolean;
}

const SpinWheel: React.FC<SpinWheelProps> = ({ prizes, rotation, gameState, onStart, theme = 'casino', buttonText = 'SPIN', isLightMode }) => {
  const isCarnival = theme === 'carnival';
  const isNeon = theme === 'neon';
  const isMinimal = theme === 'minimal' || isLightMode;
  const isRetro = theme === 'retro';

  let bgColor = 'bg-[#0b0f1a]';
  let borderColor = 'border-[#1e293b]';
  let pointerBg = 'bg-[#c29b42]';
  let pointerBorder = 'border-[#e0b953]';
  let pointerTriangle = 'border-t-[#c29b42]';
  let wheelBorder = 'border-[#c29b42]';
  let centerBg = 'bg-[#c29b42]';
  let centerBorder = 'border-[#e0b953]';
  let buttonBg = 'bg-[#c29b42] hover:bg-[#a17f33] text-[#0b0f1a]';
  let buttonTextDisplay = buttonText || 'PLACE YOUR BET';
  let centerIcon = <Crown className="w-6 h-6 text-[#0b0f1a]" />;
  let lightBulbColor = 'bg-[#fef08a]';
  let lightBulbShadow = 'shadow-[0_0_8px_#facc15]';

  if (isMinimal) {
    bgColor = 'bg-white';
    borderColor = 'border-slate-200';
    pointerBg = 'bg-slate-800';
    pointerBorder = 'border-slate-900';
    pointerTriangle = 'border-t-slate-800';
    wheelBorder = 'border-slate-800';
    centerBg = 'bg-white';
    centerBorder = 'border-slate-800';
    buttonBg = 'bg-indigo-600 hover:bg-indigo-700 text-white';
    buttonTextDisplay = buttonText || 'SPIN NOW';
    centerIcon = <Crown className="w-6 h-6 text-slate-800" />;
    lightBulbColor = 'bg-slate-300';
    lightBulbShadow = 'shadow-none';
  } else if (isCarnival) {
    bgColor = 'bg-[#5c1a1b]';
    borderColor = 'border-[#7a2224]';
    pointerBg = 'bg-white';
    pointerBorder = 'border-red-600';
    pointerTriangle = 'border-t-red-600';
    wheelBorder = 'border-[#eab308]';
    centerBg = 'bg-white';
    centerBorder = 'border-red-600';
    buttonBg = 'bg-[#eab308] hover:bg-[#ca8a04] text-[#7a2224]';
    buttonTextDisplay = buttonText || 'TRY YOUR LUCK';
    centerIcon = <Zap className="w-6 h-6 text-red-600 fill-yellow-400" />;
  } else if (isNeon) {
    bgColor = 'bg-black';
    borderColor = 'border-fuchsia-600 shadow-[0_0_15px_#c026d3]';
    pointerBg = 'bg-cyan-400';
    pointerBorder = 'border-cyan-300';
    pointerTriangle = 'border-t-cyan-400';
    wheelBorder = 'border-fuchsia-600 shadow-[0_0_20px_#c026d3]';
    centerBg = 'bg-black';
    centerBorder = 'border-cyan-400 shadow-[0_0_10px_#22d3ee]';
    buttonBg = 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white shadow-[0_0_15px_#c026d3]';
    buttonTextDisplay = buttonText || 'SPIN TO WIN';
    centerIcon = <Zap className="w-6 h-6 text-cyan-400" />;
    lightBulbColor = 'bg-cyan-300';
    lightBulbShadow = 'shadow-[0_0_10px_#67e8f9]';
  } else if (isRetro) {
    bgColor = 'bg-[#2e1065]';
    borderColor = 'border-[#4c1d95]';
    pointerBg = 'bg-[#f97316]';
    pointerBorder = 'border-[#fdba74]';
    pointerTriangle = 'border-t-[#f97316]';
    wheelBorder = 'border-[#14b8a6]';
    centerBg = 'bg-[#f97316]';
    centerBorder = 'border-[#fdba74]';
    buttonBg = 'bg-[#14b8a6] hover:bg-[#0d9488] text-white border-b-4 border-[#0f766e]';
    buttonTextDisplay = buttonText || 'PRESS START';
    centerIcon = <Zap className="w-6 h-6 text-white" />;
    lightBulbColor = 'bg-[#fdba74]';
    lightBulbShadow = 'shadow-[0_0_8px_#f97316]';
  }

  return (
    <div className={`flex flex-col items-center w-full px-6 py-12 ${bgColor} rounded-[48px] shadow-2xl relative border ${borderColor}`}>
      <div className="relative w-full aspect-square max-w-[340px] mb-12">
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-40">
           <div className={`w-14 h-16 ${pointerBg} rounded-2xl flex items-center justify-center relative border-2 ${pointerBorder} shadow-2xl`}>
              <div className={`w-0 h-0 border-l-[18px] border-l-transparent border-r-[18px] border-r-transparent border-t-[24px] ${pointerTriangle} absolute -bottom-4`} />
           </div>
        </div>
        
        <div 
          className={`w-full h-full rounded-full relative overflow-hidden transition-all duration-[4s] cubic-bezier(0.15, 0, 0.15, 1) shadow-2xl border-[12px] ${wheelBorder} z-10`}
          style={{ 
            transform: `rotate(${rotation}deg)`,
            background: `conic-gradient(${prizes.map((p, i) => `${p.color || '#6366f1'} ${i * (360 / prizes.length)}deg ${(i + 1) * (360 / prizes.length)}deg`).join(', ')})`
          }}
        >
          {prizes.map((p, i) => (
            <div key={p.id} className="absolute top-0 left-1/2 -translate-x-1/2 w-[140px] h-1/2 origin-bottom flex items-start justify-center pt-[14%]" style={{ transform: `translateX(-50%) rotate(${(360/prizes.length)*i + (360/prizes.length/2)}deg)` }}>
              <span className="font-black text-[9px] text-white uppercase transform rotate-90 tracking-tighter">{p.name}</span>
            </div>
          ))}
        </div>

        {/* Light Bulbs perfectly aligned on the border */}
        <div className="absolute inset-0 z-20 pointer-events-none">
          {Array.from({ length: 16 }).map((_, i) => {
            const angle = i * (360 / 16);
            return (
              <div
                key={i}
                className="absolute inset-0"
                style={{
                  transform: `rotate(${angle}deg)`,
                }}
              >
                <div className={`absolute top-[2px] left-1/2 -translate-x-1/2 w-2 h-2 ${lightBulbColor} rounded-full ${lightBulbShadow}`} />
              </div>
            );
          })}
        </div>

        <div className={`absolute inset-0 m-auto w-14 h-14 ${centerBg} rounded-full z-30 border-4 ${centerBorder} shadow-xl flex items-center justify-center`}>
           {centerIcon}
        </div>
      </div>
      <button 
        disabled={gameState !== 'idle'} 
        onClick={onStart} 
        className={`${buttonBg} px-12 py-6 rounded-[28px] font-black uppercase tracking-widest shadow-2xl transition-all active:scale-95`}
      >
        {gameState === 'idle' ? buttonTextDisplay : 'GOOD LUCK!'}
      </button>
    </div>
  );
};

export default SpinWheel;
